import type { SvgIconComponent } from '@mui/icons-material';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';

import type { CarHistoryEvent, CarHistoryEventTypeEnum } from '~/data/generated/model';
import { buildUrlForInvoices, buildUrlForTickets } from '~/utils/urlBuilder';

// One palette-name color drives both the timeline dot and the type chip (the
// coupling used by the disputes timeline). Icons come from @mui/icons-material.
type MuiPaletteColor = 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error';

export interface CarHistoryTypeConfig {
  label: string;
  color: MuiPaletteColor;
  icon: SvgIconComponent;
}

export const CAR_HISTORY_TYPE_CONFIG: Record<CarHistoryEventTypeEnum, CarHistoryTypeConfig> = {
  invoice: { label: 'Invoice', color: 'primary', icon: ReceiptLongOutlinedIcon },
  proposal: { label: 'Proposal', color: 'info', icon: RequestQuoteOutlinedIcon },
  credit: { label: 'Credit', color: 'success', icon: SavingsOutlinedIcon },
  ticket: { label: 'Service', color: 'secondary', icon: BuildOutlinedIcon },
  violation: { label: 'Violation', color: 'error', icon: WarningAmberOutlinedIcon },
  inspection_report: {
    label: 'Inspection',
    color: 'info',
    icon: FactCheckOutlinedIcon,
  },
  certificate: { label: 'Certificate', color: 'success', icon: VerifiedOutlinedIcon },
  contract: { label: 'Contract', color: 'warning', icon: DescriptionOutlinedIcon },
};

// The order the filter chips render in. Derived from the config so a new
// history type added to the backend enum (and to CAR_HISTORY_TYPE_CONFIG, which
// the Record type forces) automatically gets a filter chip without a second
// hand-maintained list to keep in sync.
export const CAR_HISTORY_TYPES = Object.keys(CAR_HISTORY_TYPE_CONFIG) as CarHistoryEventTypeEnum[];

// event_date is an ISO date string ("YYYY-MM-DD") or null. Format it without
// constructing a Date so a UTC-midnight parse can't shift the day in local time.
export function formatCarHistoryDate(iso: string | null): string {
  if (!iso) return 'Undated';
  const [year, month, day] = iso.split('-');
  if (!year || !month || !day) return iso;
  return `${month}/${day}/${year}`;
}

export function carHistoryStatusColor(status: string): 'success' | 'warning' | 'error' | 'default' {
  switch (status.toLowerCase()) {
    case 'resolved':
    case 'passed':
      return 'success';
    case 'open':
      return 'warning';
    case 'failed':
    case 'shutdown':
      return 'error';
    default:
      return 'default';
  }
}

/**
 * Where a timeline row links out to. There are no standalone detail routes for
 * these records — each opens on its list page via a search param. Proposals are
 * Invoice records, so their `source.kind` is `invoice` and they route to the
 * spending screen.
 *
 * Compliance-family records (violation, inspection report, certificate) and the
 * governing contract have no per-record URL. The only place they'd link to is
 * the car's compliance panel, which is where the timeline already renders, so
 * there is nowhere to navigate — those rows stay plain text.
 */
export function carHistorySourceLink(event: CarHistoryEvent): string | null {
  const { kind, id } = event.source;
  switch (kind) {
    case 'invoice':
      return buildUrlForInvoices({ invoiceId: id });
    case 'ticket':
      return buildUrlForTickets({ ticketId: id });
    default:
      return null;
  }
}
